import React, { FC, useCallback } from "react";
import cn from "classnames";
import { useField } from "formik";
import { IFormField } from "src/@types/form";
import { Label } from "./Label";
import { Error } from "../Error";
import { FormFieldVariants } from "./types";
import { ReactComponent as ArrowRightStepperIcon } from "src/assets/icons/arrow-right-stepper.svg";

const MIN = 1;
const MAX = 10000;

export const StepperField: FC<IFormField> = ({
  className,
  fieldClassName,
  labelClassName,
  id,
  label,
  min = MIN,
  max = MAX,
  ...props
}) => {
  const fieldId = id || props.name;

  const [fieldProps, fieldMeta, fieldHelpers] = useField<
    number | null | string
  >(fieldId);
  const { value, onBlur: fieldOnBlur, ...field } = fieldProps;
  const { error, touched } = fieldMeta;
  const { setValue } = fieldHelpers;

  const isShownError = Boolean((touched || value) && error);
  const numValue =
    value === null || value === ""
      ? 1
      : typeof value === "string"
        ? parseInt(value, 10) || 1
        : value;
  const displayValue = Math.max(min, Math.min(max, numValue));

  const handleDecrement = useCallback(() => {
    const next = Math.max(min, displayValue - 1);
    setValue(next);
  }, [displayValue, min, setValue]);

  const handleIncrement = useCallback(() => {
    const next = Math.min(max, displayValue + 1);
    setValue(next);
  }, [displayValue, max, setValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      setValue(null);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.max(min, Math.min(max, parsed));
      setValue(clamped);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    fieldOnBlur(e);
    if (value === null || value === "") {
      setValue(1);
    }
  };

  return (
    <div
      className={cn(
        "relative w-full flex flex-col items-start gap-4",
        {
          "outline outline-2.5 outline-offset-2 outline-red-base rounded-xl":
            isShownError,
        },
        fieldClassName
      )}
    >
      {label && (
        <Label
          className={cn("text-green-base font-black leading-none text-lg lg:text-4xl", labelClassName)}
          variant={FormFieldVariants.PRIMARY}
          htmlFor={fieldId}
        >
          {label}
        </Label>
      )}
      {/* Пілл: стрілки SVG + число */}
      <div className={cn("inline-flex items-center gap-2", className)}>
        <button
          type="button"
          aria-label="Зменшити"
          onClick={handleDecrement}
          disabled={displayValue <= min}
          className={cn(
            "flex items-center justify-center w-8 h-8 shrink-0 transition-opacity",
            "disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-70"
          )}
        >
          <span className="scale-x-[-1] inline-block">
            <ArrowRightStepperIcon width={10} height={20} />
          </span>
        </button>

        <input
          id={fieldId}
          type="number"
          min={min}
          max={max}
          value={value === null || value === "" ? "" : displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onWheel={(e) => e.currentTarget.blur()}
          style={{
            width: `${Math.max(60, String(displayValue).length * 20 + 40)}px`,
          }}
          className={cn(
            "h-[60px] rounded-2xl p-5 text-center text-base font-medium text-gray-800 bg-white border-0 outline-none transition-[width] duration-150",
            "appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          )}
          {...field}
        />

        <button
          type="button"
          aria-label="Збільшити"
          onClick={handleIncrement}
          disabled={displayValue >= max}
          className={cn(
            "flex items-center justify-center w-8 h-8 shrink-0 transition-opacity",
            "disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-70"
          )}
        >
          <ArrowRightStepperIcon width={10} height={20} />
        </button>
      </div>
      <Error isShownError={isShownError}>
        {typeof error === "string" ? error : ""}
      </Error>
    </div>
  );
};
